import React from 'react';

interface PaginationProps {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    perPage: number;
    onPageChange: (page: number) => void;
}

function Pagination({ currentPage, totalPages, totalCount, perPage, onPageChange }: PaginationProps) {
    if (totalPages <= 1) return null;

    const startItem = (currentPage - 1) * perPage + 1;
    const endItem = Math.min(currentPage * perPage, totalCount);

    const getPageNumbers = () => {
        const pages: (number | string)[] = [];
        const maxVisible = 7;

        if (totalPages <= maxVisible) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            pages.push(1);

            if (currentPage > 3) {
                pages.push('...');
            }

            const start = Math.max(2, currentPage - 1);
            const end = Math.min(totalPages - 1, currentPage + 1);

            for (let i = start; i <= end; i++) {
                pages.push(i);
            }

            if (currentPage < totalPages - 2) {
                pages.push('...');
            }

            pages.push(totalPages);
        }

        return pages;
    };

    return (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-2 py-4 sm:px-6">
            {/* Results Snapshot - Premium Typography */}
            <div className="order-2 sm:order-1">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] flex items-center gap-2">
                    <span className="w-1 h-1 bg-indigo-500 rounded-full animate-pulse"></span>
                    Displaying <span className="text-slate-900">{startItem}</span> - <span className="text-slate-900">{endItem}</span> 
                    <span className="text-slate-300 mx-1">/</span> 
                    Registry Total: <span className="text-indigo-600 underline decoration-indigo-200 underline-offset-4">{totalCount}</span>
                </p>
            </div>

            {/* Navigation Controls - High Density & Responsive */}
            <div className="order-1 sm:order-2 w-full sm:w-auto">
                <nav className="flex items-center justify-center -space-x-px" aria-label="Pagination">
                    {/* Previous Button */}
                    <button
                        onClick={() => onPageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="h-10 px-3 rounded-l-xl border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-indigo-600 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm"
                    >
                        <span className="sr-only">Previous</span>
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>

                    {/* Page Numbers - Hidden on very small screens, visible on mobile+ */}
                    <div className="hidden xs:flex -space-x-px">
                        {getPageNumbers().map((page, index) => {
                            if (page === '...') {
                                return (
                                    <span
                                        key={`ellipsis-${index}`}
                                        className="h-10 px-4 flex items-center justify-center border border-slate-200 bg-white text-[10px] font-black text-slate-400"
                                    >
                                        ...
                                    </span>
                                );
                            }
                            return (
                                <button
                                    key={page}
                                    onClick={() => onPageChange(page as number)}
                                    className={`h-10 px-4 border text-[10px] font-black transition-all ${
                                        currentPage === page
                                            ? 'z-10 bg-indigo-600 border-indigo-600 text-white shadow-lg shadow-indigo-200 scale-105'
                                            : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-indigo-600'
                                    }`}
                                >
                                    {page}
                                </button>
                            );
                        })}
                    </div>

                    {/* Simplified Mobile Display (Current / Total) */}
                    <div className="xs:hidden h-10 px-4 flex items-center justify-center border-y border-slate-200 bg-white text-[10px] font-black text-slate-700">
                        PAGE {currentPage} / {totalPages}
                    </div>

                    {/* Next Button */}
                    <button
                        onClick={() => onPageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="h-10 px-3 rounded-r-xl border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-indigo-600 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm"
                    >
                        <span className="sr-only">Next</span>
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                </nav>
            </div>
        </div>
    );
}

export default Pagination;
